import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { REASON_LABEL } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ArrowLeftRight, Pencil } from "lucide-react";
import PageHeader from "@/components/PageHeader";

export default function Links() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const [form, setForm] = useState(null);

  const { data: links } = useQuery({ queryKey: ["links"], queryFn: () => api.get("/links").then((r) => r.data), refetchInterval: 10000 });
  const { data: pops } = useQuery({ queryKey: ["pops"], queryFn: () => api.get("/pops").then((r) => r.data) });
  const popById = Object.fromEntries((pops || []).map((p) => [p.id, p]));

  const openNew = () => {
    const a = pops?.[0], b = pops?.[1];
    setForm({ name: "", pop_a: a?.id || "", pop_b: b?.id || "", iface_a: a?.interfaces?.[0]?.name || "", iface_b: b?.interfaces?.[0]?.name || "", cable_type: "Fiber Optic G.652D", capacity: "10Gbps", description: "", route: [] });
  };

  const openEdit = (l) => {
    setForm({
      id: l.id,
      name: l.name || "",
      pop_a: l.pop_a,
      pop_b: l.pop_b,
      iface_a: l.iface_a,
      iface_b: l.iface_b,
      cable_type: l.cable_type || "Fiber Optic G.652D",
      capacity: l.capacity || "10Gbps",
      description: l.description || "",
      route: l.route || [],
    });
  };

  const pickIface = (pid, current) => {
    const names = ifaces(pid).map((i) => i.name);
    if (current && names.includes(current)) return current;
    return names[0] || "";
  };

  const save = async () => {
    if (!form.pop_a || !form.pop_b) {
      toast.error("Both POPs are required");
      return;
    }
    if (form.pop_a === form.pop_b) {
      toast.error("A link must connect two different POPs");
      return;
    }
    try {
      if (form.id) {
        const body = {
          name: form.name,
          pop_a: form.pop_a,
          pop_b: form.pop_b,
          iface_a: form.iface_a,
          iface_b: form.iface_b,
          cable_type: form.cable_type,
          capacity: form.capacity,
          description: form.description,
        };
        await api.put(`/links/${form.id}`, body);
        toast.success("Link updated");
      } else {
        await api.post("/links", form);
        toast.success("Link created");
      }
      setForm(null);
      qc.invalidateQueries({ queryKey: ["links"] });
      qc.invalidateQueries({ queryKey: ["topology"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete ${l.name}?`)) return;
    await api.delete(`/links/${l.id}`);
    toast.success("Link deleted");
    qc.invalidateQueries({ queryKey: ["links"] });
    qc.invalidateQueries({ queryKey: ["topology"] });
  };

  const ifaces = (pid) => popById[pid]?.interfaces || [];
  const ifaceOptions = (pid, current) => {
    const names = ifaces(pid).map((i) => i.name).filter(Boolean);
    if (current && !names.includes(current)) names.unshift(current);
    return names.map((n) => [n, n]);
  };

  return (
    <div>
      <PageHeader
        title="Cable Links"
        description={`${links?.length || 0} backbone links · status is engine-derived, never manual`}
        actions={canEdit ? <Button data-testid="new-link-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1" /> New Link</Button> : null}
      />
      <div className="p-4 sm:p-6">
        <div className="md:hidden space-y-3">
          {(links || []).map((l) => (
            <div key={l.id} data-testid={`link-card-${l.name}`} className="rounded-md border border-border bg-card p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-heading font-bold text-sm">{l.name}</div>
                  <div className="text-[11px] font-mono text-muted-foreground mt-1 flex items-center gap-1.5">
                    <span>{popById[l.pop_a]?.code}/{l.iface_a}</span>
                    <ArrowLeftRight className="h-3 w-3" />
                    <span>{popById[l.pop_b]?.code}/{l.iface_b}</span>
                  </div>
                </div>
                <StatusBadge status={l.status} live />
              </div>
              <div className="flex items-center justify-between mt-2 text-[11px] font-mono text-muted-foreground">
                <span>{l.distance_km} km · {l.capacity}</span>
                <div className="flex items-center">
                  {canEdit && (
                    <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`link-edit-${l.name}`} onClick={() => openEdit(l)}><Pencil className="h-3.5 w-3.5" /></Button>
                  )}
                  {user?.role === "admin" && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" data-testid={`link-delete-${l.name}`} onClick={() => remove(l)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block rounded-md border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {["Link", "Endpoints", "Status", "Reason", "Distance", "Capacity", ""].map((h) => (
                  <TableHead key={h} className="text-[10px] uppercase tracking-widest text-muted-foreground">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(links || []).map((l) => (
                <TableRow key={l.id} data-testid={`link-row-${l.name}`} className="border-border hover:bg-accent/50 font-mono text-xs">
                  <TableCell className="font-heading font-bold text-sm">{l.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{popById[l.pop_a]?.code}<span className="text-muted-foreground">/{l.iface_a}</span></span>
                      <ArrowLeftRight className="h-3 w-3 text-muted-foreground" />
                      <span>{popById[l.pop_b]?.code}<span className="text-muted-foreground">/{l.iface_b}</span></span>
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={l.status} live /></TableCell>
                  <TableCell className="text-muted-foreground">{REASON_LABEL[l.status_reason] || l.status_reason}</TableCell>
                  <TableCell>{l.distance_km} km</TableCell>
                  <TableCell>{l.capacity}</TableCell>
                  <TableCell className="text-right">
                    {canEdit && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`link-edit-${l.name}`} onClick={() => openEdit(l)}><Pencil className="h-3.5 w-3.5" /></Button>
                    )}
                    {user?.role === "admin" && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" data-testid={`link-delete-${l.name}`} onClick={() => remove(l)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{form?.id ? "Edit Cable Link" : "New Cable Link"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <F label="Name" span><Input data-testid="link-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="auto" className="font-mono" /></F>
              <F label="POP A"><Sel value={form.pop_a} onChange={(v) => setForm({ ...form, pop_a: v, iface_a: pickIface(v) })} options={(pops || []).map((p) => [p.id, p.code])} testid="link-pop-a" /></F>
              <F label="POP B"><Sel value={form.pop_b} onChange={(v) => setForm({ ...form, pop_b: v, iface_b: pickIface(v) })} options={(pops || []).map((p) => [p.id, p.code])} testid="link-pop-b" /></F>
              <F label="Interface A"><Sel value={form.iface_a} onChange={(v) => setForm({ ...form, iface_a: v })} options={ifaceOptions(form.pop_a, form.iface_a)} testid="link-iface-a" /></F>
              <F label="Interface B"><Sel value={form.iface_b} onChange={(v) => setForm({ ...form, iface_b: v })} options={ifaceOptions(form.pop_b, form.iface_b)} testid="link-iface-b" /></F>
              <F label="Cable Type"><Input value={form.cable_type} onChange={(e) => setForm({ ...form, cable_type: e.target.value })} /></F>
              <F label="Capacity"><Input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="font-mono" /></F>
              <div className="sm:col-span-2 text-[11px] text-muted-foreground font-mono">
                {form.id
                  ? "Changing a POP updates the cable endpoints. Bend points on the map are kept. Use Map → Edit Link to redraw the path."
                  : "Route defaults to a straight line. Use Map → Draw Link to follow the road path."}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button data-testid="link-save" onClick={save}>{form?.id ? "Save Changes" : "Save Link"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children, span }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const Sel = ({ value, onChange, options, testid }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="font-mono"><SelectValue placeholder="—" /></SelectTrigger>
    <SelectContent>
      {options.filter(([v]) => v).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);
